import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { SECURITY_CULTURE_BOOK_DEMO } from '@/common/constants/external-urls';
import { TranslationFunctionType } from '@/common/types/locales.type';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import SCBlankPageImg from '@/images/blank-pages/sc-blank-page.svg';
import { selectCurrentAccount } from '@/store/features/account';
import { BlankPage } from '@/ui/blank-page/BlankPage';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const pathItems = (account: Account, t: TranslationFunctionType) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('security.culture'),
    url: RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](),
    template: AppBreadCrumbTemplate,
  },
];

export const SCBlankPage: React.FC = () => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { t } = useTranslation();
  const { accountCanUseFeature } = useFeatureFlag();
  const navigate = useNavigate();
  const hasSelfServiceEnabled =
    !currentAccount?.isSystem &&
    accountCanUseFeature(
      PostHogFeatureFlags.SIMPLIFIED_SC_SURVEY_PLANNER,
      currentAccount?.featureFlagAbility ?? [],
    );
  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      minHeight="100%"
    >
      <AppBreadCrumb model={pathItems(currentAccount as Account, t)} />
      <h1 className="mb-5">{t('security.culture')}</h1>
      <BlankPage
        image={SCBlankPageImg}
        title={t('security.culture.blank.heading')}
        description={t('security.culture.blank.description')}
        mainCtaText={t('security.culture.blank.cta.create')}
        mainCtaOnClick={
          hasSelfServiceEnabled
            ? () =>
                navigate(
                  RedirectPaths[
                    RedirectPathsEnum.SECURITY_CULTURE_SURVEY_PLANNER
                  ](),
                )
            : undefined
        }
        mainBookingLink={
          hasSelfServiceEnabled ? undefined : SECURITY_CULTURE_BOOK_DEMO
        }
        additionsText={[
          t('security.culture.blank.additions.1'),
          t('security.culture.blank.additions.2'),
          t('security.culture.blank.additions.3'),
        ]}
      />
    </FlexContainer>
  );
};
